import { Alert, AlertDescription } from '@ophelos/ui/components/alert';
import { Button } from '@ophelos/ui/components/button';
import { Card, CardContent } from '@ophelos/ui/components/card';
import { ArrowLeft, ChevronRight, CircleAlert, Loader2, TriangleAlert } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { PaymentMethod } from '@ophelos/shared/constants/payment-methods';
import { BottomActionBar } from '@/components/layout/BottomActionBar';
import { PaymentCountPicker } from '@/components/payments/PaymentCountPicker';
import { PayerEmailField } from '@/components/payments/PayerEmailField';
import { PaymentMethodSelectionSimple } from '@/components/payments/PaymentMethodSelectionSimple';
import { useConfiguration } from '@/contexts/ConfigurationContext';
import { useTranslation } from '@/hooks/useTranslation';
import type { BudgetAssessmentDto } from '@/lib/connect-api';
import { formatMoneyInLanguage } from '@/lib/currency';
import { buildPlanSchedule } from '@/lib/payment-schedule';
import { selectPaymentTier } from '@/lib/paymentPlans';
import { type BudgetLine, IncomeSplitBar, SummaryRow } from './CompleteStep';

/** The plan the customer arrived with, read off the deep link that sent them here. */
export interface IncomingPlan {
  frequency: 'weekly' | 'biweekly' | 'monthly';
  count: number;
  startDate: Date;
  /** What the plan has to clear, in major units. */
  totalAmount: number;
  /** The same balance before any discount. Equal to `totalAmount` when none applies. */
  originalTotalAmount: number;
  currency: string;
  /** The creditor to name, or a generic plural when an account plan spans several. */
  clientName: string;
}

interface BudgetPlanStepProps {
  assessment: BudgetAssessmentDto;
  plan: IncomingPlan;
  breakdownFor: (categories: string[]) => BudgetLine[];
  /** Submits the offer and, if it is accepted, opens checkout. */
  onContinue: (count: number, method: PaymentMethod) => void;
  /** Back to the questions, the same move the update-answers link makes. */
  onBack: () => void;
  isBusy: boolean;
  error?: string | null;
  /**
   * Payer-email capture, owned by the page: the checkout call lives there, and a second
   * `usePayerEmail()` here would hold the address the page never sees.
   */
  needsEmail: boolean;
  payerEmail: string;
  onPayerEmailChange: (value: string) => void;
  emailValid: boolean;
}

const PERIODS_PER_MONTH = { weekly: 4, biweekly: 2, monthly: 1 } as const;

/**
 * The budget summary with the customer's plan drawn into it, and a slider to move it.
 *
 * The count is bounded from both ends. Above, by the configured tier — the same cap the payment
 * page applies, so a plan chosen here can still be built. Below, by the budget itself: fewer
 * payments means a bigger instalment, and the point of showing the plan against the budget is
 * that it cannot quietly eat more than the customer has left.
 *
 * A cap on affordability is not a promise of acceptance. The length thresholds that decide
 * whether an offer needs review are deliberately withheld from the portal, so a plan that fits
 * the budget perfectly can still come back as "a specialist will review this" — which is why
 * the CTA commits to setting the plan up, not to it being agreed.
 */
export function BudgetPlanStep({
  assessment,
  plan,
  breakdownFor,
  onContinue,
  onBack,
  isBusy,
  error = null,
  needsEmail,
  payerEmail,
  onPayerEmailChange,
  emailValid,
}: BudgetPlanStepProps) {
  const { t, currentLanguage } = useTranslation();
  const { config } = useConfiguration();
  const { summary, currency } = assessment;

  const disposableCents = summary.monthly_disposable_income_cents;
  const periodsPerMonth = PERIODS_PER_MONTH[plan.frequency];

  const tier = selectPaymentTier(plan.totalAmount, config.payment?.paymentPlan);
  const maxCount = Math.max(2, (tier?.maxDurationMonths ?? 12) * periodsPerMonth);

  // The fewest payments whose monthly equivalent still fits inside what is left over, or null
  // when there is nothing left over to divide — which is not the same as "any count will do",
  // and must not be reported as a bound.
  //
  // Floor on what a period may take, ceiling on how many periods that needs: both round against
  // the customer's budget rather than in its favour, so the instalment the slider offers is never
  // a cent above what the budget was checked for.
  const minAffordableCount = useMemo(() => {
    const perPeriodCents = Math.floor(disposableCents / periodsPerMonth);
    if (perPeriodCents <= 0) return null;
    return Math.max(2, Math.ceil(Math.round(plan.totalAmount * 100) / perPeriodCents));
  }, [disposableCents, periodsPerMonth, plan.totalAmount]);

  // No length inside the tier cap fits. The count pins to the cap — the smallest instalment on
  // offer, so the customer sees the closest this can get — and the warning below says plainly
  // that it still does not fit rather than presenting worse options as choices.
  const nothingFits = minAffordableCount === null || minAffordableCount > maxCount;
  const minCount = nothingFits ? maxCount : minAffordableCount;

  const [count, setCount] = useState(() => Math.min(Math.max(plan.count, minCount), maxCount));
  useEffect(() => {
    setCount((prev) => Math.min(Math.max(prev, minCount), maxCount));
  }, [minCount, maxCount]);

  const configuredMethods = (config.payment?.planPaymentMethods ?? []) as PaymentMethod[];
  const [method, setMethod] = useState<PaymentMethod | null>(() => configuredMethods[0] ?? null);
  useEffect(() => {
    setMethod((prev) => (prev && configuredMethods.includes(prev) ? prev : (configuredMethods[0] ?? null)));
  }, [configuredMethods]);

  // The largest instalment the schedule actually contains, taken from the same builder the
  // review screen and both plan tabs use. `Math.ceil` of the division looks equivalent and is
  // not: €248 over 3 is charged as 82.66 / 82.66 / 82.68, so ceil quotes 82.67 — a figure that
  // appears nowhere in the schedule. The largest one does, and it never understates what a
  // month costs, which is the direction that matters for an affordability figure.
  //
  // It still differs from the review CTA, which quotes `instalments[0]` (82.66) because that is
  // the amount about to be charged. Two figures, two questions: what a month will cost at worst,
  // and what leaves the account first. Both are amounts the customer actually pays.
  const perPaymentCents = useMemo(
    () => Math.max(...buildPlanSchedule(plan.totalAmount, count, plan.startDate, plan.frequency).map((i) => i.subunits)),
    [plan.totalAmount, count, plan.startDate, plan.frequency],
  );
  const planMonthlyCents = perPaymentCents * periodsPerMonth;
  const leftOverCents = disposableCents - planMonthlyCents;
  const overspent = leftOverCents < 0;

  // A card plan still has the review screen ahead of it, so naming setup here would be false.
  // Every other method really does set its mandate up next.
  const ctaLabel =
    method && method !== 'card'
      ? t('paymentPlan.setupPlanWith', { method: t(`paymentMethod.${method}`) })
      : t('common.continue');

  return (
    <div className="w-full stack items-center px-4">
      <div className="flex-1 w-full max-w-sm mx-auto space-y-6 pt-6">
        {/* The journey's other steps put Back here, and this one is no more terminal than they
            are — it goes to the questions, the same move the update-answers link makes. */}
        <div>
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="size-4" />
            {t('budgetAssessment.disclosure.back')}
          </button>
        </div>

        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('budgetAssessment.plan.title')}</h1>
        </div>

        <Card>
          <CardContent className="divide-y divide-border-light">
            <SummaryRow
              label={t('budgetAssessment.complete.income')}
              cents={summary.monthly_income_cents}
              currency={currency}
              breakdown={breakdownFor(['income'])}
            />
            <SummaryRow
              label={t('budgetAssessment.complete.outgoings')}
              cents={summary.monthly_outgoings_cents}
              currency={currency}
              swatchClass="bg-chart-1"
              breakdown={breakdownFor(['essential_expenditure', 'priority_debt'])}
            />
            <SummaryRow
              label={t('budgetAssessment.plan.row')}
              cents={planMonthlyCents}
              currency={currency}
              swatchClass="bg-chart-2"
            />
            <SummaryRow
              label={t('budgetAssessment.complete.leftOver')}
              cents={leftOverCents}
              currency={currency}
              emphasis
              swatchClass={overspent ? undefined : 'bg-chart-5'}
            />
            <div className="pt-4">
              <IncomeSplitBar
                incomeCents={summary.monthly_income_cents}
                outgoingsCents={summary.monthly_outgoings_cents}
                planCents={planMonthlyCents}
                currency={currency}
              />
            </div>
          </CardContent>
        </Card>

        <PaymentCountPicker
          count={count}
          onChange={setCount}
          min={minCount}
          max={maxCount}
          disabled={isBusy}
          headingKey="budgetAssessment.plan.chooseLength"
        />

        <Alert variant="subtle">
          <CircleAlert />
          <AlertDescription>
            {t(`budgetAssessment.plan.perPayment.${plan.frequency}`, {
              amount: formatMoneyInLanguage(perPaymentCents / 100, currentLanguage, currency),
              count,
            })}
          </AlertDescription>
        </Alert>

        {overspent && (
          <Alert variant="warning">
            <TriangleAlert className="size-4" />
            <AlertDescription>
              {t(nothingFits ? 'budgetAssessment.plan.nothingFits' : 'budgetAssessment.plan.overBudget')}
            </AlertDescription>
          </Alert>
        )}

        {needsEmail && (
          <PayerEmailField id="budget-plan-payer-email" value={payerEmail} onChange={onPayerEmailChange} />
        )}

        {configuredMethods.length > 1 && method && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">{t('paymentPlan.selectPaymentMethod')}</p>
            <PaymentMethodSelectionSimple
              selectedMethod={method}
              onSelect={setMethod}
              availableMethods={configuredMethods}
              disabled={isBusy}
            />
          </div>
        )}

        {error && (
          <Alert variant="destructive">
            <CircleAlert />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </div>

      <BottomActionBar>
        <Button
          size="xl"
          className="group w-full max-w-sm"
          onClick={() => method && onContinue(count, method)}
          disabled={isBusy || !emailValid || !method}
        >
          {isBusy ? (
            <>
              <Loader2 className="size-5 mr-2 animate-spin" />
              {t('verify.processing')}
            </>
          ) : (
            <>
              {ctaLabel}
              <ChevronRight className="size-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </>
          )}
        </Button>
      </BottomActionBar>
    </div>
  );
}
